import React, {forwardRef} from 'react';

import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import styles from './state-button.component.module.styl';

import type {ComponentProps, ReactNode, Ref} from 'react';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

type CaptionType = {
    on?: ReactNode,
    off?: ReactNode,
};

type NativeInputProps = Omit<ComponentProps<'input'>, 'ref'>;

export interface Props extends NativeInputProps {
    /**
     * Captions names for state button
     */
    captions?: CaptionType,
    /**
     * Custom className for root element
     * @deprecated It will be removed in 6 months 09-15-2025. use rootProps.className instead
     */
    mix?: string,
    /**
     * Native props for the root element
     * */
    rootProps?: ComponentProps<'label'>,
    /**
     * Ref to the input element
     * */
    ref?: Ref<HTMLInputElement>,
    /**
     * Ref to root element
     * */
    rootRef?: Ref<HTMLLabelElement>,
}

/**
 * ## Accessibility
 *
 * This component implements the WAI-ARIA authoring practices for a switch role.
 * We encourage the usage of a label for the switch.
 * For more information, see [WAI-ARIA Authoring Practices](https://www.w3.org/WAI/ARIA/apg/patterns/switch/).
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-switch-state-button--documentation}
 */
export const StateButton = forwardRef<HTMLInputElement, Props>((props: Props, ref) => {
    const {
        captions,
        onChange,
        mix,
        rootProps,
        rootRef,
        className,
        ...inputProps
    } = props;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(event);
    };

    const classNamesList = classNames('state-button', mix, rootProps?.className);

    return (
        <label ref={rootRef} {...rootProps} className={classNamesList}>
            <input
                {...inputProps}
                className={classNames('state-button__handler', className)}
                ref={ref}
                role="switch"
                type="checkbox"
                onChange={handleChange}
            />
            {captions?.off ? (
                <span className={classNames('state-button__caption state-button__caption_off')} aria-hidden="true">
                    {captions.off}
                </span>
            ) : null}
            <span className={classes['state-button__box']}/>
            {captions?.on ? (
                <span className={classNames('state-button__caption state-button__caption_on')} aria-hidden="true">
                    {captions.on}
                </span>
            ) : null}
        </label>
    );
});

StateButton.displayName = 'StateButton';
